import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';
import { operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type { CompanySettings } from '@/types/settings';
import { isApiError } from '@/lib/api/errors';

export function useUploadLogo() {
  const queryClient = useQueryClient();

  return useMutation<CompanySettings, Error, File>({
    // Upload the file straight to operations (multipart); the server streams
    // it to S3 with its own credentials. This avoids a browser→S3 PUT, which
    // requires bucket CORS the app can't configure.
    //
    // NOT-124 — `operationsClient` defaults `Content-Type` to
    // `application/json` at the instance level. Without an explicit override
    // here, axios keeps that JSON header on the FormData POST, the browser
    // never gets to inject the `multipart/form-data; boundary=...` header,
    // and NestJS's `FileInterceptor` (multer) treats the body as JSON →
    // `file` is `undefined` and the controller responds 400 "No file
    // uploaded". Setting the header without a boundary tells axios to
    // delegate to FormData so the browser fills in the correct boundary.
    // Same fix shape that messaging email attachments and conversation
    // attachments already use (see `lib/messaging/api.ts` and
    // `useUploadAttachment.ts`).
    mutationFn: async (file) => {
      const form = new FormData();
      form.append('file', file);
      const res = await operationsClient.post<CompanySettings>(
        settingsEndpoints.uploadLogo(),
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return res.data;
    },
    onSuccess: (data) => {
      // On-screen settings read `logoUrl` only, so write the fresh response
      // straight into the base cache for an instant preview update.
      queryClient.setQueryData(settingsKeys.company(), data);
      // NOT-236: the PDF flow caches a separate `includeLogoDataUri` payload
      // (inline logo data URI). `setQueryData` above is exact-match and never
      // touches it, so without this the next generated PDF would keep the OLD
      // logo until that key's own staleTime lapsed. Invalidate so it refetches
      // fresh on next PDF generation.
      void queryClient.invalidateQueries({
        queryKey: settingsKeys.companyWithLogoDataUri(),
      });
      toast.success('Logo updated');
    },
    onError: (err) => {
      // Surface the real reason (size limit, MIME rejection, 401, etc.)
      // instead of a generic "Failed" toast so users can self-diagnose.
      const detail = isApiError(err)
        ? err.message
        : err instanceof Error && err.message
          ? err.message
          : null;
      toast.error(detail ? `Failed to upload logo: ${detail}` : 'Failed to upload logo');
    },
  });
}
